import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, CalendarClock, Car, CheckCircle, FileWarning, UserRound } from 'lucide-react';
import { SectionLabel } from '../ui/kit';

// ── PARTNER-UX-HEUTE-AKTEN-EINHEIT-001 (Phase 2, Owner-Ansage 30.08.2026): „Heute"-
// Handlungsliste des Partners — dasselbe Muster wie die Admin-Startseite: NUR Offenes,
// jede Zeile springt direkt in den Bereich, nichts offen = grüne Ruhemeldung.
// EIGENE Datei, damit die von LEGAL-HUB beanspruchte Dashboard.js nur eine Einhäng-Zeile
// braucht (Owner hat Phase 2 trotz aktiver LEGAL-HUB-Karte ausdrücklich angeordnet).
// Datenquellen sind die vom Dashboard BEREITS geladenen /partners/cockpit-Zähler und
// /partners/dashboard-warnings — bewusst KEIN neuer Endpunkt (cockpit IST die Aggregation).
//
// REVIEW-FIX (Zweiter Kopf, P2-Blocker): JEDE Warnung ist eine EIGENE Zeile mit dem
// vollen Wortlaut (kein „nur der erste Text je Gruppe" mehr — die Zielseiten kennen
// keinen „läuft bald ab"-Zustand, also muss der Text HIER vollständig lesbar sein).
// Sprungziel kommt strukturiert aus warning.scope ('vehicle' → /vehicles, sonst
// /drivers) — kein Text-Parsen; Fallback ohne scope ist /drivers.

const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

function Row({ icon: Icon, count, label, detail, wrap = false, onClick }) {
  const [h, setH] = React.useState(false);
  return (
    <button type="button" onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ display: 'grid', gridTemplateColumns: count == null ? 'auto minmax(0,1fr) auto' : 'auto auto minmax(0,1fr) auto', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '11px 14px', background: h ? '#faf9fb' : '#fff', border: 'none', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}>
      <span style={{ display: 'grid', width: 30, height: 30, placeItems: 'center', borderRadius: 10, background: '#f4f5f6', color: '#4b5563', alignSelf: 'start' }}><Icon size={16} strokeWidth={1.8} /></span>
      {count != null && (
        <span style={{ display: 'grid', width: 30, height: 30, placeItems: 'center', borderRadius: 10, border: '1px solid #eadced', background: '#fbf6fc', color: '#9c27b0', fontSize: 12, fontWeight: 800 }}>{count}</span>
      )}
      <span style={{ minWidth: 0 }}>
        <strong style={{ display: 'block', overflow: 'hidden', color: '#0a0a0a', fontSize: 13, fontWeight: 750, textOverflow: wrap ? 'clip' : 'ellipsis', whiteSpace: wrap ? 'normal' : 'nowrap' }}>{label}</strong>
        {detail && <small style={{ display: 'block', color: '#6b7280', fontSize: 12 }}>{detail}</small>}
      </span>
      <ArrowUpRight size={14} color="#6b7280" style={{ alignSelf: 'start', marginTop: 4 }} />
    </button>
  );
}

// Sprungziel je Warnung: strukturiert über scope, fail-safe zum Fahrer-Bereich.
const warningRoute = (warning) => (warning && warning.scope === 'vehicle' ? '/vehicles' : '/drivers');

export default function TodayPanel({ cockpit, warnings = [] }) {
  const navigate = useNavigate();
  const fleet = cockpit?.fleet || {};
  const list = (Array.isArray(warnings) ? warnings : []).filter((w) => w && w.text);

  const countRows = [
    { icon: Car, count: n(fleet.vehicles_review), label: 'Fahrzeuge in Prüfung', detail: 'Freigabe durch VOOGO steht aus', route: '/vehicles' },
    { icon: CalendarClock, count: n(fleet.open_reservations), label: 'Reservierungen offen', detail: 'Vorbestellungen im Blick behalten', route: '/reservations' },
  ].filter((row) => row.count > 0);
  const warningRows = list.map((warning, index) => ({
    key: `warnung-${index}`,
    icon: warning.level === 'expiry' ? FileWarning : UserRound,
    label: warning.text,
    detail: warning.level === 'expiry' ? 'Läuft bald ab — rechtzeitig erneuern' : 'Fahrer-Hinweis',
    route: warningRoute(warning),
  }));
  const total = countRows.reduce((sum, row) => sum + row.count, 0) + warningRows.length;

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <SectionLabel>Jetzt zu erledigen</SectionLabel>
        <span style={{ fontSize: 12, fontWeight: 800, color: total > 0 ? '#9c27b0' : '#6b7280' }}>{total} offen</span>
      </div>
      {total === 0 ? (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 10, background: '#f0faf3', border: '1px solid #d3ecd9', color: '#1f7a38', fontSize: 13, fontWeight: 700 }}>
          <CheckCircle size={16} /> Nichts offen — alles erledigt.
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          {countRows.map((row) => (
            <Row key={row.label} {...row} onClick={() => navigate(row.route)} />
          ))}
          {warningRows.map((row) => (
            <Row key={row.key} icon={row.icon} count={null} label={row.label} detail={row.detail} wrap onClick={() => navigate(row.route)} />
          ))}
        </div>
      )}
    </div>
  );
}
